import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/api';
import { Message, ChatSession, FaqCategory, LocalMessage, ChatMode } from '../chat.types';

interface UseChatSessionOptions {
  onSendMessageStart?: () => void;
}

export const useChatSession = (options: UseChatSessionOptions = {}) => {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState('');
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [showSessions, setShowSessions] = useState(false);
  const [selectedFaqCategory, setSelectedFaqCategory] = useState<FaqCategory | null>(null);
  const [faqFlowStep, setFaqFlowStep] = useState<'categories' | 'items' | 'answer'>('categories');
  const [chatMode, setChatMode] = useState<ChatMode>('initial');
  const [faqMessages, setFaqMessages] = useState<LocalMessage[]>([]);
  const [lastAiMessageId, setLastAiMessageId] = useState<number | null>(null);
  const [showSampleRequestGuide, setShowSampleRequestGuide] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Fetch sessions
  const { data: sessionsData } = useQuery({
    queryKey: ['chatSessions'],
    queryFn: async () => {
      const response = await api.get('/chat/sessions');
      return response.data?.data || [];
    },
    refetchOnWindowFocus: false
  });
  const sessions: ChatSession[] = sessionsData || [];

  // Fetch FAQ categories
  const { data: faqCategoriesData } = useQuery({
    queryKey: ['publicFaqCategories'],
    queryFn: async () => {
      const response = await api.get('/faq/categories');
      return response.data?.data || [];
    },
    staleTime: 60000,
    refetchOnWindowFocus: false
  });
  const faqCategories: FaqCategory[] = faqCategoriesData || [];

  // Fetch messages for current session
  const { data: messagesData, isLoading: messagesLoading } = useQuery({
    queryKey: ['chatMessages', currentSessionId],
    queryFn: async () => {
      if (!currentSessionId) return [];
      const response = await api.get(`/chat/sessions/${currentSessionId}/messages`);
      return response.data?.data || [];
    },
    enabled: !!currentSessionId,
    refetchOnWindowFocus: false,
  });
  const messages: Message[] = useMemo(() => messagesData || [], [messagesData]);

  // Send message mutation
  const sendMessageMutation = useMutation({
    mutationFn: async (text: string) => {
      const endpoint = currentSessionId
        ? `/chat/sessions/${currentSessionId}/messages`
        : '/chat/quick-message';
      const response = await api.post(endpoint, { message: text, sessionId: currentSessionId || '' });
      return response.data?.data;
    },
    onMutate: () => {
      options.onSendMessageStart?.();
    },
    onSuccess: (data) => {
      if (data?.sessionId && !currentSessionId) {
        setCurrentSessionId(data.sessionId);
      }
      if (data?.assistantMessage?.id) {
        setLastAiMessageId(data.assistantMessage.id);
      }
      queryClient.invalidateQueries({ queryKey: ['chatMessages', currentSessionId || data?.sessionId] });
      queryClient.invalidateQueries({ queryKey: ['chatSessions'] });
    },
  });

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-focus input
  useEffect(() => {
    inputRef.current?.focus();
  }, [currentSessionId]);

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    const text = message.trim();
    if (!text || sendMessageMutation.isPending) return;
    setMessage('');
    setChatMode('question');
    setSelectedFaqCategory(null);
    sendMessageMutation.mutate(text);
  }, [message, sendMessageMutation]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as unknown as React.FormEvent);
    }
  }, [handleSubmit]);

  const handleNewChat = useCallback(() => {
    setCurrentSessionId(null);
    setShowSessions(false);
    setSelectedFaqCategory(null);
    setFaqFlowStep('categories');
    setShowSampleRequestGuide(false);
    setChatMode('initial');
    setFaqMessages([]);
    setLastAiMessageId(null);
  }, []);

  const handleFaqCategoryClick = useCallback((category: FaqCategory) => {
    const now = new Date().toISOString();
    const userMsgId = Date.now();

    const userMsg: LocalMessage = {
      id: userMsgId,
      role: 'user',
      content: category.name,
      created_at: now,
      animate: false,
    };

    setFaqMessages(prev => [
      ...prev.map(msg => ({ ...msg, animate: false })),
      userMsg
    ]);
    setSelectedFaqCategory(category);
    setFaqFlowStep('items');

    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);

  const handleFaqItemClick = useCallback((item: { id: number; question: string; answer: string }) => {
    const now = new Date().toISOString();
    const userMsgId = Date.now();
    const assistantMsgId = Date.now() + 1;

    const userMsg: LocalMessage = {
      id: userMsgId,
      role: 'user',
      content: item.question,
      created_at: now,
      animate: false,
    };
    const assistantMsg: LocalMessage = {
      id: assistantMsgId,
      role: 'assistant',
      content: item.answer,
      created_at: now,
      animate: true,
    };

    setFaqMessages(prev => [
      ...prev.map(msg => ({ ...msg, animate: false })),
      userMsg,
      assistantMsg
    ]);
    setFaqFlowStep('answer');
    setSelectedFaqCategory(null);

    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);

  const handleBackToCategories = useCallback(() => {
    setFaqFlowStep('categories');
    setSelectedFaqCategory(null);
  }, []);

  const handleSelectSession = useCallback((sessionId: string) => {
    setCurrentSessionId(sessionId);
    setShowSessions(false);
    setLastAiMessageId(null);
  }, []);

  const handleStartSampleRequest = useCallback(() => {
    setChatMode('sample-request');
    setShowSampleRequestGuide(true);
    setSelectedFaqCategory(null);
  }, []);

  const handleStartQuestion = useCallback(() => {
    setChatMode('question');
    setShowSampleRequestGuide(false);
    setSelectedFaqCategory(null);
  }, []);

  const handleBackToInitial = useCallback(() => {
    setChatMode('initial');
    setShowSampleRequestGuide(false);
    setSelectedFaqCategory(null);
  }, []);

  return {
    // State
    message,
    currentSessionId,
    showSessions,
    selectedFaqCategory,
    faqFlowStep,
    chatMode,
    faqMessages,
    lastAiMessageId,
    showSampleRequestGuide,
    sessions,
    faqCategories,
    messages,
    messagesLoading,
    sendMessageMutation,
    messagesEndRef,
    inputRef,

    // Setters
    setMessage,
    setShowSessions,
    setChatMode,
    setShowSampleRequestGuide,

    // Handlers
    handleSubmit,
    handleKeyDown,
    handleNewChat,
    handleFaqCategoryClick,
    handleFaqItemClick,
    handleBackToCategories,
    handleSelectSession,
    handleStartSampleRequest,
    handleStartQuestion,
    handleBackToInitial,
  };
};
